import type { ConversationHistoryItem } from "@/features/chat-history/conversationHistorySchemas";
import type { MerryChatActivityItem } from "@/shared/ui/merryChatActivityModel";

export interface ConversationHistorySearchState {
  query: string;
  view: "active" | "archived";
}

export function mergeConversationHistoryPages(
  pages: ReadonlyArray<{ items: ReadonlyArray<ConversationHistoryItem> }> | undefined,
) {
  const items = new Map<string, ConversationHistoryItem>();
  for (const page of pages ?? []) {
    for (const item of page.items) {
      if (!items.has(item.id)) items.set(item.id, item);
    }
  }
  return [...items.values()];
}

export function parseConversationHistorySearch(
  search: string,
): ConversationHistorySearchState {
  const params = new URLSearchParams(search);
  const rawView = params.getAll("view");
  const rawQuery = params.getAll("query");
  const hasUnknownParameter = Array.from(params.keys()).some(
    (key) => key !== "view" && key !== "query",
  );
  if (
    hasUnknownParameter ||
    rawView.length > 1 ||
    rawQuery.length > 1 ||
    (rawView.length === 1 &&
      rawView[0] !== "active" &&
      rawView[0] !== "archived" &&
      rawView[0] !== "")
  ) {
    return { query: "", view: "active" };
  }
  return {
    query: normalizeConversationHistorySearchQuery(rawQuery[0] ?? ""),
    view:
      rawView.length === 1 && rawView[0] === "archived"
        ? "archived"
        : "active",
  };
}

export function buildConversationHistorySearch(
  state: ConversationHistorySearchState,
) {
  const params = new URLSearchParams();
  if (state.view === "archived") params.set("view", "archived");
  const query = normalizeConversationHistorySearchQuery(state.query);
  if (query) params.set("query", query);
  const encoded = params.toString();
  return encoded ? `?${encoded}` : "";
}

export function normalizeConversationHistorySearchQuery(value: string) {
  return Array.from(value.trim().replace(/\s+/gu, " "))
    .slice(0, 120)
    .join("");
}

export function isConversationHistoryTitleValid(value: string) {
  const title = value.trim();
  return title.length > 0 && Array.from(title).length <= 120;
}

export function conversationHistoryTime(value: string | null) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return new Intl.DateTimeFormat("zh-CN", {
      hour: "2-digit",
      hour12: false,
      minute: "2-digit",
    }).format(date);
  }
  return new Intl.DateTimeFormat("zh-CN", {
    day: "numeric",
    month: "numeric",
    year: date.getFullYear() === now.getFullYear() ? undefined : "numeric",
  }).format(date);
}

export function toMerryChatActivityItem(
  item: ConversationHistoryItem,
): MerryChatActivityItem {
  return {
    id: item.id,
    preview: item.preview,
    status: item.activityStatus,
    time: conversationHistoryTime(item.lastMessageAt ?? item.updatedAt),
    title: item.title,
  };
}
